import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import MaskedReveal from "./MaskedReveal";

const HERO_IMG = "https://images.unsplash.com/photo-1680624629496-0a2c2444153e?crop=entropy&cs=srgb&fm=jpg&w=2400&q=90&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxjaW5lbWF0aWMlMjB3ZWRkaW5nJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg0MjI5MjU2fDA&ixlib=rb-4.1.0";

export default function CinematicHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-[var(--bg)]" data-testid="cinematic-hero">
      {/* Background image w/ parallax + ken burns */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
      >
        <img
          src={HERO_IMG}
          alt="Cinematic frame"
          className="w-full h-full object-cover ken-burns"
          draggable={false}
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 -z-10 bg-gradient-to-b from-black/70 via-black/50 to-black"
      />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,transparent_0%,var(--bg)_85%)]" />

      {/* Top-left meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.9 }}
        className="absolute top-28 left-6 md:left-12 z-10 font-sans-display text-[10px] tracking-[0.4em] uppercase text-[var(--ink-muted)]"
        data-testid="hero-meta-left"
      >
        <div className="text-[var(--gold)] mb-2">↳ Est. MMXVIII</div>
        <div>Reel · 2026 / Vol. 04</div>
      </motion.div>

      {/* Top-right meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.9 }}
        className="absolute top-28 right-6 md:right-12 z-10 text-right font-sans-display text-[10px] tracking-[0.4em] uppercase text-[var(--ink-muted)]"
      >
        <div>49°N · 122°W</div>
        <div className="text-[var(--gold)] mt-2">On Set — Live</div>
      </motion.div>

      {/* Centered kinetic type */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 flex flex-col items-center justify-center min-h-[100svh] px-6 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="h-eyebrow mb-8"
        >
          Sebastian Visual Corp — Cinematic Studio
        </motion.div>

        <h1 className="h-display text-[13vw] md:text-[10vw] lg:text-[8.4vw] leading-[0.9] tracking-[-0.03em] text-[var(--ink)] max-w-[1400px]">
          <MaskedReveal className="block" delay={0.6}>Your Vision.</MaskedReveal>
          <MaskedReveal className="block italic font-serif-editorial text-[var(--gold)] font-light" delay={0.9} stagger={0.06}>
            Masterfully Engineered.
          </MaskedReveal>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-8 md:mt-10 font-serif-editorial text-lg md:text-xl leading-relaxed text-[var(--ink)]/80 max-w-xl"
        >
          A small, disciplined studio building cinema, stills and aerial work for people who refuse to micromanage their storyteller.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.9 }}
          className="mt-12 flex flex-col sm:flex-row items-center gap-6"
        >
          <Link
            to="/contact"
            data-testid="hero-cta-commission"
            className="group inline-flex items-center gap-3 bg-[var(--royal)] text-white px-8 py-4 font-sans-display uppercase tracking-[0.28em] text-[11px] hover:bg-[var(--royal-soft)] transition-colors"
          >
            Commission a Project
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </Link>
          <Link
            to="/portfolio"
            data-testid="hero-cta-portfolio"
            className="group inline-flex items-center gap-3 text-[var(--ink)] border-b border-white/20 hover:border-[var(--gold)] pb-2 font-sans-display uppercase tracking-[0.28em] text-[11px]"
          >
            View the Reel
            <span className="transition-transform group-hover:translate-x-1">↳</span>
          </Link>
        </motion.div>
      </motion.div>

      {/* Bottom scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
        aria-hidden
      >
        <div className="font-sans-display text-[10px] tracking-[0.4em] uppercase text-[var(--ink-muted)]">Scroll</div>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[var(--gold)] to-transparent"
        />
      </motion.div>
    </section>
  );
}
